import Logo from '@/assets/icons/logo.svg?react';
import Telegram from '@/assets/icons/telegram.svg?react';
import Whatsapp from '@/assets/icons/whatsapp.svg?react';
import Phone from '@/assets/icons/phone.svg?react';
import NavbarIcon from '@/assets/icons/navbar.svg?react';

import { NavLink, useLocation } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { IconButton } from '@/shared/ui/Button/IconButton';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { selectMenuItems } from '../model/selectors';
import { openMobileSidebar, setMenuItemActive } from '../model/slice';
import { contactsType } from '@/entities/home_content/model/types';
import { useEffect, useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { useScrollToTopOnRouteChange } from '@/shared/hooks/useScrollToTopOnRouteChange';
import { CookieBanner } from '@/features/cookieConsent/ui/CookieBanner';

type NavbarProps = {
  contacts?: contactsType;
  visible?: boolean;
  handleAccept?: () => void;
};

export default function Navbar({
  contacts,
  visible,
  handleAccept,
}: NavbarProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const url = location.pathname;

  const menu = useAppSelector(selectMenuItems);
  const [isFixed, setIsFixed] = useState<boolean>(false);

  const handleShowMobileSidebar = () => {
    dispatch(openMobileSidebar());
  };

  useScrollToTopOnRouteChange();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      setIsFixed(scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    dispatch(setMenuItemActive({ url }));
  }, [dispatch, url]);

  return (
    <div
      className={cn(
        'fixed left-0 top-0 z-20 w-full border-b-[0.5px] border-black',
        isFixed && 'bg-white'
      )}
      style={{ position: 'fixed' }}
    >
      <CookieBanner visible={visible} handleAccept={handleAccept} />
      <div
        className={cn(
          'container mx-auto flex h-[60px] flex-row items-center justify-between gap-[24px] px-[10px] md:h-[70px] md:px-[32px] 2xl:px-[180px] 3xl:px-[290px]'
        )}
      >
        <NavLink to="/">
          <Logo className="h-[22px] w-[141px]" />
        </NavLink>

        <nav className="hidden md:flex md:flex-row">
          <ul className="flex items-center gap-[8px] lg:gap-[32px]">
            {menu &&
              menu.map((item, index) => (
                <li key={index}>
                  <NavLink
                    to={item.link}
                    className={cn(
                      'normal whitespace-nowrap transition-colors hover:text-[var(--black-grey)]',
                      item.active && 'text-[var(--black-grey)]'
                    )}
                  >
                    {item.name}
                  </NavLink>
                </li>
              ))}
          </ul>
        </nav>

        <div className="flex items-center gap-[24px]">
          <div className="flex items-center gap-[8px] lg:gap-[12px]">
            <a href={contacts?.telegramUrl} target="_block">
              <IconButton
                className="flex h-min w-min"
                icon={<Telegram className="h-[36px] w-[35px]" />}
              />
            </a>

            <a href={contacts?.whatsappUrl} target="_block">
              <Whatsapp className="h-[36px] w-[35px]" />
            </a>

            <a
              href={`tel:${contacts ? contacts.phoneNumbers[0] : '+7 (495)137-97-65'}`}
              className="hidden lg:flex"
            >
              <Button
                startIcon={<Phone className="stroke-white" />}
                text={contacts ? contacts.phoneNumbers[0] : '+7 (495)137-97-65'}
                className="flex h-[40px] bg-black px-[28px] text-[14px] text-white"
                bg="white"
              />
            </a>
            <a
              href={`tel:${contacts ? contacts.phoneNumbers[0] : '+7 (495)137-97-65'}`}
              className="block lg:hidden"
            >
              <IconButton
                icon={<Phone className="h-[15px]" />}
                className="box-border flex h-[36px] w-[36px] border border-black"
              />
            </a>
          </div>

          <IconButton
            icon={<NavbarIcon />}
            className="box-border md:hidden"
            onClick={handleShowMobileSidebar}
          />
        </div>
      </div>
    </div>
  );
}
